import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Logo from "../components/Logo";
import GoogleSignIn from "../components/GoogleSignIn";
import { useAuth } from "../context/AuthContext";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";

function MagicLinkForm() {
    const [email, setEmail] = useState("");
    const [sent, setSent] = useState(false);
    const [busy, setBusy] = useState(false);
    const send = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/auth/magic/request", { email });
            setSent(true);
        } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    if (sent) return <p className="max-w-md text-sm text-neutral-600" data-testid="magic-sent">If an account exists for <b>{email}</b>, a one-time login link is on its way. Check your inbox (and spam).</p>;
    return (
        <form onSubmit={send} className="max-w-md space-y-3" data-testid="magic-form">
            <p className="text-sm text-neutral-500">Bought something, signed up with Google, or forgot your password? Get a one-time login link by email.</p>
            <input required type="email" placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="magic-email" className="w-full border border-neutral-300 px-4 py-3.5 outline-none focus:border-ink" />
            <button disabled={busy} className="btn-outline w-full !normal-case disabled:opacity-60" data-testid="magic-submit">{busy ? "Sending…" : "Email me a login link"}</button>
        </form>
    );
}

function GoogleBlock({ welcome }) {
    const { loginWithGoogle } = useAuth();
    const navigate = useNavigate();
    const go = async (credential) => {
        const u = await loginWithGoogle(credential);
        toast.success(welcome);
        navigate(u.role === "admin" ? "/admin" : "/dashboard");
    };
    return (
        <GoogleSignIn onSuccess={go} text="continue_with">
            <Divider />
        </GoogleSignIn>
    );
}

function AuthShell({ children, title, sub }) {
    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-white">
            <div className="hidden lg:block relative bg-navy grain">
                <img loading="lazy" decoding="async" src="https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="" className="absolute inset-0 h-full w-full object-cover grayscale-[0.3] opacity-50" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/40 to-navy/20" />
                <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
                    <Logo theme="dark" size="md" />
                    <div>
                        <h2 className="font-display font-black uppercase tracking-tightest text-4xl leading-[0.95]">Build your<br /><span className="text-brand-yellow">artist career.</span></h2>
                        <p className="mt-4 text-white/60 max-w-sm">Create, release, build and grow, everything under one roof.</p>
                    </div>
                </div>
            </div>
            <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-12">
                <div className="lg:hidden mb-10"><Logo size="md" /></div>
                <Link to="/" className="inline-flex items-center gap-1.5 text-neutral-400 hover:text-ink text-sm mb-8"><ArrowLeft className="h-4 w-4" /> Back to site</Link>
                <h1 className="font-display font-black uppercase tracking-tightest text-4xl">{title}</h1>
                <p className="mt-2 text-neutral-500">{sub}</p>
                {children}
            </div>
        </div>
    );
}

export function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState({ email: "", password: "" });
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const u = await login(form.email, form.password);
            toast.success("Welcome back!");
            navigate(u.role === "admin" ? "/admin" : "/dashboard");
        } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    return (
        <AuthShell title="Log In" sub="Welcome back to your artist command center.">
            <GoogleBlock welcome="Welcome back!" />
            <form onSubmit={submit} className="mt-8 space-y-4 max-w-md" data-testid="login-form">
                <input required type="email" placeholder="Email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="login-email" className="w-full border border-neutral-300 px-4 py-3.5 outline-none focus:border-ink" />
                <input required type="password" placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="login-password" className="w-full border border-neutral-300 px-4 py-3.5 outline-none focus:border-ink" />
                <button disabled={busy} className="btn-primary w-full disabled:opacity-60" data-testid="login-submit">{busy ? "Logging in…" : "Log In"}</button>
            </form>
            <Divider />
            <MagicLinkForm />
            <p className="mt-8 text-sm text-neutral-500">No account? <Link to="/signup" className="link-blue" data-testid="to-signup">Sign up</Link></p>
        </AuthShell>
    );
}

export function Signup() {
    const { register } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState({ name: "", email: "", password: "" });
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await register(form.name, form.email, form.password);
            toast.success("Account created. Let's build!");
            navigate("/dashboard");
        } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    return (
        <AuthShell title="Sign Up" sub="Start your artist journey with MUVIARTS.">
            <GoogleBlock welcome="You're in. Let's build!" />
            <form onSubmit={submit} className="mt-8 space-y-4 max-w-md" data-testid="signup-form">
                <input required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="signup-name" className="w-full border border-neutral-300 px-4 py-3.5 outline-none focus:border-ink" />
                <input required type="email" placeholder="Email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="signup-email" className="w-full border border-neutral-300 px-4 py-3.5 outline-none focus:border-ink" />
                <input required type="password" placeholder="Password (min 10 chars)" minLength={10} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="signup-password" className="w-full border border-neutral-300 px-4 py-3.5 outline-none focus:border-ink" />
                <button disabled={busy} className="btn-primary w-full disabled:opacity-60" data-testid="signup-submit">{busy ? "Creating…" : "Create Account"}</button>
            </form>
            <p className="mt-8 text-sm text-neutral-500">Already have an account? <Link to="/login" className="link-blue" data-testid="to-login">Log in</Link></p>
        </AuthShell>
    );
}

const Divider = () => (
    <div className="max-w-md flex items-center gap-4 my-6"><span className="flex-1 h-px bg-neutral-200" /><span className="text-xs text-neutral-400 uppercase tracking-wide">or</span><span className="flex-1 h-px bg-neutral-200" /></div>
);
export { Login as default };
